import { Bell } from '@phosphor-icons/react';
import { Popup, VText } from '@vake/ui-kit';
import { Feature } from 'geojson';
import { FC, useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Tour } from 'react-shepherd';
import styled from 'styled-components';
import { useGetReportInfo } from '../../../../requests/actor-mode';
import { formatRelativeTime } from '../../../../shared/components/time';
import constants from '../../../../shared/constants';
import { Button, ButtonLink } from '../../../../shared/styles/Buttons';
import { Spinner } from '../../../../shared/styles/Spinner';
import { Insights } from '../../../../utils/interfaces';
import { ConfidenceSlider } from '../../../../utils/Slider';
import { SendToMattermost } from '../../components/send-to-mattermost';
import { ColumnWrapper, PaddingWrapper, RoundedWrapper } from '../../styles/Wrappers';
import { DateSelector } from './DateSelector';
import { MoreInfo } from './MoreInfo';
import { SearchForMmsi } from './SearchForMmsi';

const SideBar = styled.div`
    position: absolute;
    top: 50px;
    left: 5px;
    z-index: 1000;
    min-width: 175px;
    width: 25%;
    max-width: 325px;
    label {
        margin-bottom: 12px;
    }
    @media screen and (max-width: ${constants.breakpoints.mobile}) {
        display: none;
    }
`;

const MeetingBtn = styled(ButtonLink)`
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    margin-top: 20px;
`;

const Mobile = styled(RoundedWrapper)`
    @media screen and (min-width: ${constants.breakpoints.mobile}) {
        display: none;
    }
`;
const BannerRow = styled.div<{ background?: string }>`
    display: flex;
    padding: 25px;
    align-items: center;
    justify-content: space-between;
    background: ${(props) => (props.background ? props.background : constants.colors.pallet.lightBlack)};
`;
const BottomBanner = styled.div<{ background?: string }>`
    position: fixed;
    bottom: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    width: 100vw;
    ${BannerRow} {
        padding: 5px 15px;
    }
`;

export const ArrowWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

export const Arrow = styled.div<{ disabled?: boolean }>`
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    display: flex;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    width: 35px;
    height: 35px;
    align-items: center;
    justify-content: center;
    img {
        opacity: ${(props) => props.disabled && '0.5'};
    }
    &:hover {
        box-shadow: ${(props) => !props.disabled && 'inset 0 0 100px 100px rgba(255, 255, 255, 0.2)'};
    }
`;

export const TimeSinceLastConfirmedAis = styled.div`
    position: fixed;
    margin: 5em;
    margin-top: 5em;
    padding: 2em;
    width: 10%;
    background: ${constants.colors.pallet.lightBlack};
    border-radius: 5px;
    border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Border = styled(PaddingWrapper)`
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled(PaddingWrapper)`
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    flex-direction: row;
`;

export const QuestionBall = styled.div`
    height: 16px;
    width: 16px;
    border-radius: 17px;
    background-color: #666666;
    align-self: center;
    padding: 2px;
    font-size: 15px;
    text-align: center;
    margin-left: 10px;
    line-height: 18px;
`;

export interface IReportInfo {
    reportName: string;
    index: number;
    setIndex: (index: number) => void;
    availableDates: Date[];
    insights: Insights | undefined;
    visibleLayers: string[];
    setVisibleLayers: (layers: string[]) => void;
    tour: Tour;
    detectionConfidence: number;
    setDetectionConfidence: (confidence: number) => void;
    setSelectedFeature: (feature: Feature | null) => void;
    setProcessingStatus: (callback: (bool: boolean) => boolean) => void;
    numAcqusitions: number;
}

type RouteParams = {
    id: string;
};
export const ReportInfo: FC<IReportInfo> = (props) => {
    const {
        reportName,
        availableDates,
        setIndex,
        index,
        insights,
        visibleLayers,
        setVisibleLayers,
        tour,
        detectionConfidence,
        setDetectionConfidence,
        setSelectedFeature,
        setProcessingStatus,
        numAcqusitions,
    } = props;
    const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
    const params = useParams<RouteParams>();
    const location = useLocation();

    const {
        data: dataReport,
        isError: isErrorReport,
        isLoading: isLoadingReport,
        isSuccess: isSuccessReport,
    } = useGetReportInfo(params.id);
    const [lastItem, setLastItem] = useState<boolean>(false);

    // useEffect for setting last item true or false
    useEffect(() => {
        if (availableDates!.length - 1 === index) {
            setLastItem(true);
        } else {
            setLastItem(false);
        }
    }, [availableDates, index]);

    const prevLayer = () => {
        setOpenDatePicker(false);
        if (index > 0) {
            setIndex(index - 1);
        }
    };
    const nextLayer = () => {
        setOpenDatePicker(false);
        if (!lastItem) {
            setIndex(index + 1);
        }
    };

    const chosenDate =
        availableDates && availableDates.length > 0 && index !== -1
            ? availableDates[index].toISOString().split('T')[0]
            : '';
    const reportHasBegan = !!(
        isSuccessReport &&
        dataReport.subscriptionStart &&
        (dataReport.subscriptionEnd === undefined || dataReport.subscriptionStart < new Date())
    );

    return (
        <>
            <Mobile>
                <BottomBanner>
                    <BannerRow>
                        {availableDates && availableDates.length === 0 ? (
                            <Spinner />
                        ) : (
                            <>
                                <span>{reportName}</span>
                            </>
                        )}
                    </BannerRow>
                    {availableDates && availableDates.length !== 0 && (
                        <BannerRow background={constants.colors.pallet.blue}>
                            <VText>{chosenDate}</VText>
                            <ArrowWrapper>
                                <Arrow disabled={index === 0} onClick={() => prevLayer()}>
                                    <img src="/images/icons/ArrowLeft.svg" alt="Previous date" />
                                </Arrow>
                                <Arrow disabled={lastItem} onClick={() => nextLayer()}>
                                    <img src="/images/icons/ArrowRight.svg" alt="Next date" />
                                </Arrow>
                            </ArrowWrapper>
                        </BannerRow>
                    )}
                </BottomBanner>
            </Mobile>
            <Popup
                isOpen={isSuccessReport && !reportHasBegan}
                tooglePopup={function (): void {
                    throw new Error('Function not implemented.');
                }}
            >
                <VText variant="H3">Oops...</VText>
                <VText>This report has the following time range</VText>
                <VText>
                    {dataReport?.subscriptionStart ? formatRelativeTime(dataReport.subscriptionStart) : 'start'} -{' '}
                    {dataReport?.subscriptionEnd ? formatRelativeTime(dataReport.subscriptionEnd) : 'continuous'}
                </VText>
                <VText>The report has not started yet, please come back later</VText>
                <SendToMattermost
                    textOnButton="Notify Vake"
                    iconOnButton={<Bell size={16} />}
                    textToSend={`Created an [Overwatch](${window.location.href}) for the future. Contact the user to investigate what was intended.`}
                    successMessage={`Thank you, we've received your message. We'll be in touch soon.`}
                />
            </Popup>
            <SideBar>
                <RoundedWrapper>
                    <Title id="report_name">
                        <div className="flex flex-col">
                            <div className="flex">
                                <VText variant="H1">{reportName}</VText>
                                <QuestionBall onClick={tour.start}>?</QuestionBall>
                            </div>
                            {isLoadingReport && <VText>Loading dates</VText>}
                            {isErrorReport && <VText>Error loading dates</VText>}
                            {isSuccessReport && (
                                <VText>
                                    Period:{' '}
                                    {dataReport?.subscriptionStart
                                        ? formatRelativeTime(dataReport.subscriptionStart)
                                        : 'start'}{' '}
                                    -{' '}
                                    {dataReport?.subscriptionEnd
                                        ? formatRelativeTime(dataReport.subscriptionEnd)
                                        : 'continuous'}
                                </VText>
                            )}
                        </div>
                    </Title>
                    {availableDates && availableDates.length > 0 && (
                        <Border id="date_selector">
                            <DateSelector
                                {...props}
                                openDatePicker={openDatePicker}
                                setOpenDatePicker={setOpenDatePicker}
                            />
                        </Border>
                    )}
                    <ConfidenceSlider
                        detectionConfidence={detectionConfidence}
                        setDetectionConfidence={setDetectionConfidence}
                    />
                    <SearchForMmsi reportDetectionsData={insights} setSelectedFeature={setSelectedFeature} />
                </RoundedWrapper>
                <MoreInfo
                    {...props}
                    reportName={reportName}
                    insights={insights}
                    lastItem={lastItem}
                    nextLayer={nextLayer}
                    prevLayer={prevLayer}
                    chosenDate={chosenDate}
                    visibleLayers={visibleLayers}
                    setVisibleLayers={setVisibleLayers}
                    setProcessingStatus={setProcessingStatus}
                    detectionConfidence={detectionConfidence}
                    reportHasBegan={reportHasBegan}
                    numAcqusitions={numAcqusitions}
                />
                {location.pathname === '/report' && (
                    <ColumnWrapper>
                        <MeetingBtn target="_blank" href="https://calendly.com/vake/vake-intro" background="#2C2E33">
                            Book a meeting with us
                        </MeetingBtn>
                        <Link style={{ width: 'inherit' }} to="/report-builder">
                            <Button style={{ width: 'calc(100% - 5px' }} background="white">
                                Request a free Report
                            </Button>
                        </Link>
                    </ColumnWrapper>
                )}
            </SideBar>
        </>
    );
};
